import React, { useState } from "react";
import { Box, Paper, Typography, MenuItem, Select, FormControl, Skeleton, Chip } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import { IBillingMetricsData } from "../interfaces/billingSubscriptions.interface";

interface BillingOverviewCardsProps {
  metrics?: IBillingMetricsData;
  isLoading?: boolean;
}

export const BillingOverviewCards: React.FC<BillingOverviewCardsProps> = ({ metrics, isLoading }) => {
  const [selectedSection, setSelectedSection] = useState<string>("all");

  const currentMetrics =
    selectedSection !== "all" && metrics?.sections?.[selectedSection]
      ? metrics.sections[selectedSection]!
      : {
          totalActiveSubscriptions: metrics?.totalActiveSubscriptions ?? 0,
          totalFreeTrials: metrics?.totalFreeTrials ?? 0,
          totalCancelled: metrics?.totalCancelled ?? 0,
          estimatedMRR: metrics?.estimatedMRR ?? 0,
          totalSubscriptions: (metrics?.totalActiveSubscriptions ?? 0) + (metrics?.totalFreeTrials ?? 0) + (metrics?.totalCancelled ?? 0),
        };

  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(1)}k`;
    }
    return `$${amount}`;
  };

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "var(--fx-surface-text, #0F172A)" }}>
            Billing Overview
          </Typography>
          {selectedSection !== "all" && (
            <Chip
              label={selectedSection === "flow_subscription" ? "Flow Subscription" : "Flow Mobile"}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: "11px" }}
            />
          )}
        </Box>

        <FormControl size="small">
          <Select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            sx={{
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: 600,
              color: "var(--fx-surface-textsecondary, #64748B)",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--fx-surface-border, #E2E8F0)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "var(--fx-surface-borderstrong, #CBD5E1)",
              },
            }}
          >
            <MenuItem value="all">All Sections</MenuItem>
            <MenuItem value="flow_subscription">Flow Subscription</MenuItem>
            <MenuItem value="flow_mobile">Flow Mobile</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Metrics Cards Grid */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between", // Cambiado de alignContent a justifyContent
          alignItems: "center",
          width: "100%",
          gap: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "none",
            width: "230px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", letterSpacing: "0.5px" }}>
              ESTIMATED MRR
            </Typography>
            <TrendingUpIcon sx={{ fontSize: 18, color: "#16a34a" }} />
          </Box>

          {isLoading ? (
            <Skeleton variant="text" width={80} height={40} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
              {formatCurrency(currentMetrics.estimatedMRR)}
            </Typography>
          )}

          <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600, mt: 0.5, display: "block" }}>
            Monthly Recurring Revenue
          </Typography>
        </Paper>

        {/* Metric 2: ACTIVE SUBSCRIPTIONS */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "none",
            width: "230px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", letterSpacing: "0.5px" }}>
              ACTIVE
            </Typography>
            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2563eb" }} />
          </Box>

          {isLoading ? (
            <Skeleton variant="text" width={60} height={40} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
              {currentMetrics.totalActiveSubscriptions}
            </Typography>
          )}

          <Typography variant="caption" sx={{ color: "#2563eb", fontWeight: 600, mt: 0.5, display: "block" }}>
            Active subscription{currentMetrics.totalActiveSubscriptions !== 1 ? "s" : ""}
          </Typography>
        </Paper>

        {/* Metric 3: FREE TRIALS */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "none",
            width: "230px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", letterSpacing: "0.5px" }}>
              FREE TRIALS
            </Typography>
            <CardGiftcardIcon sx={{ fontSize: 18, color: "#9333ea" }} />
          </Box>

          {isLoading ? (
            <Skeleton variant="text" width={60} height={40} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
              {currentMetrics.totalFreeTrials}
            </Typography>
          )}

          <Typography variant="caption" sx={{ color: "#9333ea", fontWeight: 600, mt: 0.5, display: "block" }}>
            Trial subscription{currentMetrics.totalFreeTrials !== 1 ? "s" : ""}
          </Typography>
        </Paper>

        {/* Metric 4: CANCELLED */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: "16px",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "none",
            width: "230px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: 700, color: "var(--fx-surface-textsecondary, #64748B)", letterSpacing: "0.5px" }}>
              CANCELLED
            </Typography>
            <CancelOutlinedIcon sx={{ fontSize: 18, color: "#dc2626" }} />
          </Box>

          {isLoading ? (
            <Skeleton variant="text" width={60} height={40} />
          ) : (
            <Typography variant="h4" sx={{ fontWeight: 800, color: "var(--fx-surface-text, #0F172A)" }}>
              {currentMetrics.totalCancelled}
            </Typography>
          )}

          <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600, mt: 0.5, display: "block" }}>
            Cancelled subscription{currentMetrics.totalCancelled !== 1 ? "s" : ""}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};
